// src/pages/SimulaRosaPage.js
// "Simula rosa": costruisci una rosa ipotetica scegliendo giocatori dal
// listone completo (fuori da qualunque asta) e valutane fantamedia/media
// prevista, copertura per reparto e confronto stagione corrente/precedente.
// All'ingresso si configurano crediti, sistema ruoli (classic/mantra) e
// limiti per ruolo: da lì in poi la tabella e le statistiche si adattano al
// sistema scelto. La selezione vive solo in questa sessione (nessun
// salvataggio): per conservarla si esporta direttamente in un'asta reale a
// partecipante singolo, ereditando ruoli, limiti e i crediti impostati per
// ogni giocatore.
import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import TeamBadge from '../components/TeamBadge';
import AppTooltip from '../components/Tooltip';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { ensureFantasyDataSynced, getPlayersFromFirestore, getLineupStatusByPlayerId } from '../firebase/playerService';
import { applyFantAppRoleOverrides } from '../utils/fantAppRoleOverrides';
import {
  createAuction,
  savePurchase,
  normalizeRoleOverridesInput,
  DEFAULT_ROSTER_LIMITS,
} from '../firebase/auctionService';
import {
  RoleChip,
  ROLE_ORDER,
  ROLE_LABELS,
  ROLE_CHIP_THEME,
  fmt,
  v,
  getCurrentSeasonWeight,
  getPlayProbabilityLabel,
  getPlayProbabilitySortValue,
} from '../components/auction/auctionCommon';
import {
  getUpcomingFixturesWithDifficulty,
  getDifficultyColor,
  formatDifficultyValue,
  formatFixtureDate,
} from '../data/serieACalendar';
import { DEFAULT_MANTRA_ROLE_CONFIGURATION, MANTRA_TOKEN_ORDER, MANTRA_ROLE_THEME } from '../components/auction/MantraRoleConfiguration';
import { FANTAPP_ROLE_OVERRIDES_CONFIGURATION } from '../components/auction/FantAppRoleConfiguration';
import { buildRoleOverrideMap, normalizeNameText, splitRoleTokens } from '../components/auction/auctionRosterUtils';
import AuctionMantraManageTab from '../components/auction/AuctionMantraManageTab';
import SimulaRosaExcelImport from '../components/SimulaRosaExcelImport';
import {
  ArrowLeft,
  Search,
  X,
  Loader2,
  FlaskConical,
  Users,
  Trophy,
  Shirt,
  ExternalLink,
  Settings2,
  ShieldCheck,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  FileSpreadsheet,
  RotateCcw,
} from 'lucide-react';
import './SimulaRosaPage.css';

const PAGE_SIZE = 60;
const ROLE_FILTERS = ['all', 'P', 'D', 'C', 'A'];
const DEFAULT_MOVEMENT_LIMIT = DEFAULT_ROSTER_LIMITS.D + DEFAULT_ROSTER_LIMITS.C + DEFAULT_ROSTER_LIMITS.A;
// Stessa soglia usata in AuctionFormationTab.js per la distribuzione per
// squadra: oltre questo numero di giocatori dalla stessa squadra reale la
// rosa dipende troppo dal calendario di quel singolo club.
const TEAM_CONCENTRATION_THRESHOLD = 3;

function normalizeText(value = '') {
  const normalized = String(value).toLowerCase().trim().normalize('NFD');
  let result = '';
  for (const ch of normalized) {
    const code = ch.codePointAt(0);
    if (code >= 0x0300 && code <= 0x036f) continue;
    result += ch;
  }
  return result;
}

// Media reale (markavg/fmarkavg) per ruolo, calcolata solo sui giocatori con
// almeno un dato osservato (corrente o precedente) — usata come riferimento
// per stimare i giocatori senza storico. Nessun numero magico: la baseline
// arriva sempre dal listone effettivamente caricato.
function buildRoleBaselines(players) {
  const acc = {};
  ROLE_ORDER.forEach((role) => { acc[role] = { mv: 0, fv: 0, value: 0, n: 0 }; });

  players.forEach((p) => {
    const role = ROLE_ORDER.includes(p.role) ? p.role : 'A';
    const mv = v(p.markavg) || v(p.previousSeasonStats?.markavg);
    const fv = v(p.fmarkavg) || v(p.previousSeasonStats?.fmarkavg);
    if (!mv && !fv) return;
    acc[role].mv += mv;
    acc[role].fv += fv;
    acc[role].value += v(p.value);
    acc[role].n += 1;
  });

  const baselines = {};
  ROLE_ORDER.forEach((role) => {
    const { mv, fv, value, n } = acc[role];
    baselines[role] = n > 0
      ? { mv: mv / n, fv: fv / n, value: (value / n) || 1 }
      : { mv: 6, fv: 6, value: 1 };
  });
  return baselines;
}

// Giocatore senza statistiche della stagione precedente (esordiente, nuovo
// acquisto dall'estero, ecc.): stima fantamedia/media voto attese a partire
// dalla media di ruolo, corretta in base al suo valore di quotazione
// relativo alla media di ruolo (più costoso del "giocatore medio" del suo
// ruolo → stima più alta, e viceversa). Il fattore di scala è smorzato
// (0.5 + 0.5×rapporto, rapporto clampato 0.5–2×) per evitare stime estreme
// da un singolo numero — resta una stima, non va mai mostrata come dato
// reale nelle tabelle di dettaglio, solo usata nei valori aggregati in alto.
function estimateStatForNewPlayer(player, field, roleBaselines) {
  const role = ROLE_ORDER.includes(player.role) ? player.role : 'A';
  const baseline = roleBaselines[role];
  const baselineStat = field === 'markavg' ? baseline.mv : baseline.fv;
  const valueRatio = baseline.value > 0 ? v(player.value) / baseline.value : 1;
  const dampedRatio = Math.min(2, Math.max(0.5, valueRatio || 1));
  return baselineStat * (0.5 + 0.5 * dampedRatio);
}

// Media pesata tra stagione corrente e "riferimento" (precedente se
// disponibile, altrimenti la stima valore+ruolo qui sopra), più peso alla
// corrente quanto più presenze ha già accumulato quest'anno (stessa logica
// usata altrove nell'app per gli score AI, vedi getCurrentSeasonWeight in
// auctionCommon.js).
function getBlendedStat(player, field, roleBaselines) {
  const current = v(player?.[field]);
  const prevStats = player?.previousSeasonStats;
  const reference = prevStats ? v(prevStats[field]) : estimateStatForNewPlayer(player, field, roleBaselines);
  const w = getCurrentSeasonWeight(player?.caps);
  return current * w + reference * (1 - w);
}

// Ogni giocatore in rosa ha DUE prezzi, come in un fantacalcio a listone:
//  - quotazione ATTUALE: il prezzo a cui si compra o si VENDE oggi (sempre
//    modificabile; default = quotazione del listone, o la colonna "Quotazione
//    Attuale" dell'export importato);
//  - costo d'ACQUISTO: quanto è stato pagato, fissato quando entra in rosa
//    (default = la quotazione attuale in quel momento; dall'export = colonna
//    "Costo d'acquisto").
// Vendere = incassare la quotazione attuale: la differenza col costo
// d'acquisto è la plusvalenza (o minusvalenza) che torna nel budget.
function getCurrentValue(player, currentById) {
  const override = currentById[String(player?.id)];
  return Number.isFinite(override) ? override : Math.round(v(player?.value));
}

function getPurchaseCost(player, purchaseById, currentById) {
  const override = purchaseById[String(player?.id)];
  return Number.isFinite(override) ? override : getCurrentValue(player, currentById);
}

function fmtDelta(n) {
  const r = Math.round(n);
  return `${r > 0 ? '+' : ''}${r}`;
}

function round1(n) {
  return Math.round(Number(n || 0) * 10) / 10;
}

// Stessa funzione usata in AuctionDetailPage.js per aprire la scheda
// giocatore: pagina generica /players/:id, non legata a un'asta specifica,
// quindi riusabile qui pari pari.
function openPlayerDetailNewTab(playerId) {
  window.open(`${window.location.origin}/players/${playerId}`, '_blank', 'noopener,noreferrer');
}

function ChartTooltip({ active, payload, label, isDark }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: isDark ? '#151c36' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(255,255,255,.12)' : 'rgba(16,24,40,.12)'}`,
        borderRadius: 10,
        padding: '8px 10px',
        fontSize: 12,
      }}
    >
      <strong style={{ display: 'block', marginBottom: 4 }}>{label}</strong>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {fmt(p.value)}
        </div>
      ))}
    </div>
  );
}

// Ruoli Mantra di un giocatore. DEFAULT_MANTRA_ROLE_CONFIGURATION non elenca i
// portieri: per loro il ruolo Mantra è sempre "P" (stesso fallback di
// StrategyEditPage), altrimenti comparirebbero senza ruolo e sparirebbero dai filtri.
function getMantraTokens(player, mantraRoleMap) {
  const tokens = splitRoleTokens(mantraRoleMap?.[normalizeNameText(player?.name || '')] || '');
  if (tokens.length) return tokens;
  return player?.role === 'P' ? ['P'] : [];
}

// Prossime 3 partite della squadra: stemma avversario + C/T, bordo colorato
// in base alla difficoltà (stessa scala del listone nell'asta).
const FIXTURE_COUNT = 3;

function FixtureMini({ team, fixtures }) {
  if (!fixtures?.length) return <span className="sr-fix sr-fix--empty">—</span>;
  return (
    <span className="sr-fix">
      {fixtures.map((f) => (
        <AppTooltip
          key={f.giornata}
          as="span"
          className="sr-fix__m"
          style={{ '--d': getDifficultyColor(f.difficulty) }}
          content={`G${f.giornata} · ${formatFixtureDate(f.date)} · ${f.home ? `${team}-${f.opponent}` : `${f.opponent}-${team}`} · difficoltà ${formatDifficultyValue(f.difficulty)}/5`}
        >
          <TeamBadge team={f.opponent} size={20} showTooltip={false} />
          <i>{f.home ? 'C' : 'T'}</i>
        </AppTooltip>
      ))}
    </span>
  );
}

// Ordinamento della lista giocatori: chiave → valore confrontabile.
const SORT_OPTIONS = [
  { key: 'name', label: 'Giocatore', dir: 'asc' },
  { key: 'team', label: 'Squadra', dir: 'asc' },
  { key: 'role', label: 'Ruolo', dir: 'asc' },
  { key: 'prob', label: 'Titolarità', dir: 'desc' },
  { key: 'value', label: 'Quotazione', dir: 'desc' },
  { key: 'fixtures', label: 'Calendario (più facile prima)', dir: 'asc' },
];

function SortHeader({ label, sortKey, sort, onSort, className = '' }) {
  const active = sort.key === sortKey;
  return (
    <th className={className} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className={`sr-sort-btn ${active ? 'is-active' : ''}`} onClick={() => onSort(sortKey)}>
        {label}
        <span className="sr-sort-btn__arrow" aria-hidden="true">{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );
}

function MantraRoleTokens({ player, mantraRoleMap }) {
  const tokens = getMantraTokens(player, mantraRoleMap);

  if (!tokens.length) {
    return <span className="sr-mantra-chip sr-mantra-chip--empty">—</span>;
  }

  return (
    <span className="sr-mantra-chips">
      {tokens.map((token) => {
        const theme = MANTRA_ROLE_THEME[token] || MANTRA_ROLE_THEME.A;
        return (
          <span
            key={token}
            className="sr-mantra-chip"
            style={{ background: theme.bg, border: `1px solid ${theme.border}`, color: theme.color }}
          >
            {token}
          </span>
        );
      })}
    </span>
  );
}

// Probabile formazione FantaMaster per la prossima giornata (stesso dato
// usato in AuctionUnavailableTab/AuctionMarketTab): entry = { status:
// 'starter'|'bench', prob: number|null } oppure undefined se non disponibile.
function TitolaritaChip({ entry }) {
  const label = getPlayProbabilityLabel(entry);
  const variant = !entry || label === '—' ? 'unknown' : entry.status === 'starter' ? 'starter' : 'bench';
  // Etichetta corta per il telefono ("90%"): il colore dice già titolare/panchina.
  const short = variant !== 'unknown' && Number.isFinite(Number(entry?.prob)) ? `${entry.prob}%` : label;
  return (
    <span className={`sr-playprob-chip sr-playprob-chip--${variant}`}>
      <span className="sr-playprob__full">{label}</span>
      <span className="sr-playprob__short">{short}</span>
    </span>
  );
}

function CoverageBar({ label, count, max, color }) {
  const over = max > 0 && count > max;
  const pct = max > 0 ? Math.min(100, Math.round((count / max) * 100)) : 0;
  return (
    <div className="sr-coverage-row">
      <span className="sr-coverage-row__label">{label}</span>
      <div className="sr-coverage-row__track">
        <span className="sr-coverage-row__fill" style={{ width: `${pct}%`, background: over ? '#ef4444' : color }} />
      </div>
      <span className={`sr-coverage-row__value ${over ? 'is-over' : ''}`}>{count}/{max}</span>
    </div>
  );
}

function StatTile({ label, value, hint, warn }) {
  return (
    <div className="sr-stat-tile">
      <span className="sr-stat-tile__label">{label}</span>
      <strong className={`sr-stat-tile__value ${warn ? 'is-warn' : ''}`}>{value}</strong>
      {hint && <span className="sr-stat-tile__hint">{hint}</span>}
    </div>
  );
}

// Input numerico con le freccine su/giù ridisegnate nello stile dell'app: i
// browser non permettono di ricolorare quelle native del sistema operativo,
// solo nasconderle (vedi CSS ::-webkit-inner-spin-button/-moz-appearance),
// quindi qui le sostituiamo con due bottoncini reali.
function NumberStepper({ value, onChange, min = 0, step = 1, className = '' }) {
  const current = Number(value) || 0;
  const commit = (next) => onChange(Math.max(min, next));

  return (
    <span className={`sr-stepper ${className}`.trim()}>
      <input
        type="number"
        inputMode="numeric"
        min={min}
        value={value}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => commit(Number(e.target.value) || 0)}
      />
      <span className="sr-stepper__btns">
        <button
          type="button"
          tabIndex={-1}
          aria-label="Aumenta"
          onClick={(e) => { e.stopPropagation(); commit(current + step); }}
        >
          <ChevronUp size={11} />
        </button>
        <button
          type="button"
          tabIndex={-1}
          aria-label="Diminuisci"
          onClick={(e) => { e.stopPropagation(); commit(current - step); }}
        >
          <ChevronDown size={11} />
        </button>
      </span>
    </span>
  );
}

// ─── SCHERMATA DI SETUP ─────────────────────────────────────────────────────
// Prima di poter selezionare giocatori occorre sapere crediti, sistema ruoli
// e limiti per ruolo: servono per calcolare budget residuo, copertura e per
// pre-compilare correttamente l'export verso un'asta reale.
function SetupScreen({ rolesMode, onRolesModeChange, totalCredits, onCreditsChange, roleLimits, onLimitChange, onConfirm }) {
  const canConfirm = Number(totalCredits) > 0;

  return (
    <div className="sr-setup glass">
      <div className="sr-setup__head">
        <FlaskConical size={22} />
        <div>
          <strong>Imposta la tua asta ipotetica</strong>
          <span>Servono per calcolare copertura, budget residuo e per un eventuale export in un'asta vera.</span>
        </div>
      </div>

      <label className="sr-modal__field">
        <span>Crediti a disposizione</span>
        <NumberStepper min={1} value={totalCredits} onChange={onCreditsChange} />
      </label>

      <div className="sr-modal__field">
        <span>Sistema ruoli</span>
        <div className="sr-modal__roleToggle">
          <button
            type="button"
            className={rolesMode === 'classic' ? 'is-active' : ''}
            onClick={() => onRolesModeChange('classic')}
          >
            Classic
          </button>
          <button
            type="button"
            className={rolesMode === 'mantra' ? 'is-active' : ''}
            onClick={() => onRolesModeChange('mantra')}
          >
            Mantra
          </button>
        </div>
      </div>

      <div className="sr-modal__field">
        <span>Limiti per ruolo</span>
        <div className="sr-setup__limitsGrid">
          {rolesMode === 'classic' ? (
            ROLE_ORDER.map((role) => (
              <label key={role} className="sr-setup__limitField">
                <span>{ROLE_LABELS[role]}</span>
                <NumberStepper value={roleLimits[role]} onChange={(val) => onLimitChange(role, val)} />
              </label>
            ))
          ) : (
            <>
              <label className="sr-setup__limitField">
                <span>Portieri</span>
                <NumberStepper value={roleLimits.P} onChange={(val) => onLimitChange('P', val)} />
              </label>
              <label className="sr-setup__limitField">
                <span>Giocatori di movimento</span>
                <NumberStepper value={roleLimits.M} onChange={(val) => onLimitChange('M', val)} />
              </label>
            </>
          )}
        </div>
      </div>

      <button type="button" className="sr-export-btn" onClick={onConfirm} disabled={!canConfirm}>
        Inizia la simulazione
      </button>
    </div>
  );
}

// Conferma cessione: prima di vendere si sceglie a che prezzo — quello
// d'acquisto (nessuna plusvalenza) o quello attuale, modificabile.
function SellModal({ player, purchase, current, onClose, onConfirm }) {
  const [mode, setMode] = useState('current');
  const [price, setPrice] = useState(current);

  useEffect(() => {
    setMode('current');
    setPrice(current);
  }, [player, current]);

  if (!player) return null;
  const sale = mode === 'purchase' ? purchase : Math.max(0, Number(price) || 0);
  const delta = sale - purchase;

  return (
    <div className="sr-modal-backdrop" onClick={onClose}>
      <div className="sr-modal glass" onClick={(e) => e.stopPropagation()}>
        <div className="sr-modal__head">
          <strong>Cedi {player.name}</strong>
          <button type="button" className="sr-modal__close" onClick={onClose} aria-label="Chiudi">
            <X size={16} />
          </button>
        </div>
        <p className="sr-modal__desc">A che prezzo vuoi cederlo? Il ricavato torna nei tuoi crediti disponibili.</p>

        <div className="sr-sell-options">
          <label className={`sr-sell-option ${mode === 'purchase' ? 'is-active' : ''}`}>
            <input type="radio" name="sr-sell-mode" checked={mode === 'purchase'} onChange={() => setMode('purchase')} />
            <span className="sr-sell-option__text">
              <strong>Prezzo d'acquisto</strong>
              <em>{purchase} cr · nessuna plusvalenza</em>
            </span>
          </label>
          <label className={`sr-sell-option ${mode === 'current' ? 'is-active' : ''}`}>
            <input type="radio" name="sr-sell-mode" checked={mode === 'current'} onChange={() => setMode('current')} />
            <span className="sr-sell-option__text">
              <strong>Prezzo attuale</strong>
              <em>quotazione di oggi, modificabile</em>
            </span>
            <span onClick={() => setMode('current')}>
              <NumberStepper min={0} value={price} onChange={(val) => { setMode('current'); setPrice(val); }} />
            </span>
          </label>
        </div>

        <p className="sr-sell-summary">
          Incassi <b>{sale}</b> cr · pagato {purchase} ·{' '}
          <b className={delta > 0 ? 'is-pos' : delta < 0 ? 'is-neg' : ''}>
            {delta > 0 ? `plusvalenza ${fmtDelta(delta)}` : delta < 0 ? `minusvalenza ${fmtDelta(delta)}` : 'pari'}
          </b>
        </p>

        <div className="sr-modal__actions">
          <button type="button" className="sr-modal__cancel" onClick={onClose}>Annulla</button>
          <button type="button" className="sr-modal__confirm" onClick={() => onConfirm(sale)}>Cedi a {sale} cr</button>
        </div>
      </div>
    </div>
  );
}

function ExportModal({ open, onClose, defaultCredits, onConfirm, exporting, error }) {
  const [name, setName] = useState('');
  const [teamName, setTeamName] = useState('La mia rosa');
  const [credits, setCredits] = useState(defaultCredits);

  useEffect(() => {
    if (open) {
      setName(`Simulazione ${new Date().toLocaleDateString('it-IT')}`);
      setTeamName('La mia rosa');
      setCredits(defaultCredits);
    }
  }, [open, defaultCredits]);

  if (!open) return null;

  return (
    <div className="sr-modal-backdrop" onClick={onClose}>
      <div className="sr-modal glass" onClick={(e) => e.stopPropagation()}>
        <div className="sr-modal__head">
          <strong>Esporta in un'asta</strong>
          <button type="button" className="sr-modal__close" onClick={onClose} aria-label="Chiudi">
            <X size={16} />
          </button>
        </div>
        <p className="sr-modal__desc">
          Verrà creata una nuova asta a partecipante singolo, stesso sistema ruoli e limiti impostati qui,
          con i giocatori selezionati già acquistati ai crediti scelti per ciascuno (modificabili dopo, dentro l'asta).
        </p>

        <label className="sr-modal__field">
          <span>Nome asta</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
        </label>

        <label className="sr-modal__field">
          <span>Nome squadra</span>
          <input type="text" value={teamName} onChange={(e) => setTeamName(e.target.value)} maxLength={40} />
        </label>

        <label className="sr-modal__field">
          <span>Budget crediti</span>
          <NumberStepper min={1} value={credits} onChange={setCredits} />
        </label>

        {error && <p className="sr-modal__error">{error}</p>}

        <div className="sr-modal__actions">
          <button type="button" className="sr-modal__cancel" onClick={onClose} disabled={exporting}>
            Annulla
          </button>
          <button
            type="button"
            className="sr-modal__confirm"
            disabled={exporting || !name.trim() || !teamName.trim() || Number(credits) <= 0}
            onClick={() => onConfirm({ name: name.trim(), teamName: teamName.trim(), credits: Number(credits) })}
          >
            {exporting ? <Loader2 size={15} className="spin" /> : null}
            {exporting ? 'Creazione asta...' : 'Crea asta'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SimulaRosaPage() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]);
  const [playProbabilityById, setPlayProbabilityById] = useState({});
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [roleFilter, setRoleFilter] = useState('all');
  const [mantraRoleFilter, setMantraRoleFilter] = useState(() => new Set());
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Sottoinsieme dei preferiti (selectedIds) effettivamente incluso nel
  // calcolo delle statistiche in basso: aggiungere un giocatore ai preferiti
  // non lo include automaticamente nell'analisi, va marcato esplicitamente
  // con la checkbox nella riga (richiesta esplicita dell'utente — permette
  // di confrontare sottoinsiemi diversi della stessa rosa di preferiti).
  const [analyzedIds, setAnalyzedIds] = useState(() => new Set());
  const [isDragOverRoster, setIsDragOverRoster] = useState(false);
  const [purchaseById, setPurchaseById] = useState({}); // costo d'acquisto
  const [currentById, setCurrentById] = useState({}); // quotazione attuale (override)
  // Giocatori ceduti: { [id]: { purchase, sale } } — sale = prezzo di vendita
  // (default la quotazione attuale al momento della cessione, modificabile).
  const [soldById, setSoldById] = useState({});
  const [soldOpen, setSoldOpen] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // ─── Setup: crediti, sistema ruoli, limiti per ruolo ───────────────────
  const [setupComplete, setSetupComplete] = useState(false);
  const [rolesMode, setRolesMode] = useState('classic');
  const [totalCredits, setTotalCredits] = useState(500);
  const [roleLimits, setRoleLimits] = useState({
    P: DEFAULT_ROSTER_LIMITS.P,
    D: DEFAULT_ROSTER_LIMITS.D,
    C: DEFAULT_ROSTER_LIMITS.C,
    A: DEFAULT_ROSTER_LIMITS.A,
    M: DEFAULT_MOVEMENT_LIMIT,
  });

  const handleLimitChange = (role, value) => {
    setRoleLimits((current) => ({ ...current, [role]: Math.max(0, Number(value) || 0) }));
  };

  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);

  // Import da Excel (rose di Fantacalcio.it): i giocatori entrano nella rosa
  // col costo d'acquisto del file e, se c'è, con la quotazione attuale
  // (colonna "Quotazione Attuale"), e sono subito inclusi nell'analisi — è una
  // squadra intera, non una lista di preferiti da valutare uno a uno.
  // "Sostituisci" riparte da zero, anche con le cessioni.
  const handleExcelImport = ({ items, mode }) => {
    const ids = items.map((it) => String(it.playerId));
    setSelectedIds((current) => new Set(mode === 'add' ? [...current, ...ids] : ids));
    setAnalyzedIds((current) => new Set(mode === 'add' ? [...current, ...ids] : ids));
    setPurchaseById((current) => {
      const next = mode === 'add' ? { ...current } : {};
      items.forEach((it) => { next[String(it.playerId)] = Math.max(0, Number(it.credits) || 0); });
      return next;
    });
    setCurrentById((current) => {
      const next = mode === 'add' ? { ...current } : {};
      items.forEach((it) => {
        if (it.currentValue !== null && it.currentValue !== undefined) next[String(it.playerId)] = Math.max(0, Number(it.currentValue) || 0);
      });
      return next;
    });
    setSoldById((current) => {
      if (mode !== 'add') return {};
      const next = { ...current };
      ids.forEach((id) => { delete next[id]; });
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        let list = await getPlayersFromFirestore().catch(() => []);
        if (!list.length) {
          await ensureFantasyDataSynced();
          list = await getPlayersFromFirestore().catch(() => []);
        }
        if (!cancelled) {
          // FantAppRoleConfiguration.js contiene il ruolo classic corretto
          // per i nomi noti al listone FantApp: applicarlo qui garantisce
          // che la colonna Classic mostri sempre il ruolo giusto, come nel
          // resto dell'app.
          const normalized = applyFantAppRoleOverrides(list);
          setPlayers(normalized);

          // Fire-and-forget: dipende da un endpoint esterno non sotto il
          // nostro controllo (a volte lento/instabile) e non deve mai
          // bloccare il caricamento del listone (stesso pattern usato in
          // AuctionDetailPage.js).
          getLineupStatusByPlayerId(normalized)
            .then((data) => { if (!cancelled) setPlayProbabilityById(data?.byId || {}); })
            .catch(() => {});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Stesso meccanismo già usato in StrategyEditPage per mostrare il ruolo
  // Mantra fuori da un'asta specifica: match esatto per nome (niente
  // collisioni tra omonimi come i due Thuram).
  const mantraRoleMap = useMemo(
    () => buildRoleOverrideMap({ roleOverrides: DEFAULT_MANTRA_ROLE_CONFIGURATION }),
    []
  );

  const roleBaselines = useMemo(() => buildRoleBaselines(players), [players]);

  const playersById = useMemo(() => {
    const map = new Map();
    players.forEach((p) => map.set(String(p.id), p));
    return map;
  }, [players]);

  // Mantra: filtro sui ruoli Mantra (Dd, Dc, E, W, T, Pc...), a selezione
  // multipla — passa chi ha ALMENO uno dei ruoli scelti (stesso criterio del
  // filtro del listone nell'asta). Classic: P/D/C/A a scelta singola.
  const filteredPlayers = useMemo(() => {
    const query = normalizeText(deferredSearch);
    return players.filter((p) => {
      if (rolesMode === 'mantra') {
        if (mantraRoleFilter.size) {
          const tokens = getMantraTokens(p, mantraRoleMap);
          if (!tokens.some((t) => mantraRoleFilter.has(t))) return false;
        }
      } else if (roleFilter !== 'all') {
        const role = ROLE_ORDER.includes(p.role) ? p.role : 'A';
        if (role !== roleFilter) return false;
      }
      if (!query) return true;
      return normalizeText(p.name).includes(query) || normalizeText(p.team).includes(query);
    });
  }, [players, roleFilter, mantraRoleFilter, rolesMode, mantraRoleMap, deferredSearch]);

  // Prossime partite per squadra (una sola volta per club, non per giocatore).
  const fixturesByTeam = useMemo(() => {
    const map = new Map();
    players.forEach((p) => {
      if (p.team && !map.has(p.team)) map.set(p.team, getUpcomingFixturesWithDifficulty(p.team, FIXTURE_COUNT));
    });
    return map;
  }, [players]);

  // Ordinamento per colonna (click sull'intestazione, o menu su telefono).
  // Senza scelta resta l'ordine del listone.
  const sortValue = (p, key) => {
    switch (key) {
      case 'name': return normalizeText(p.name);
      case 'team': return normalizeText(p.team);
      case 'role': {
        if (rolesMode === 'mantra') {
          const idx = getMantraTokens(p, mantraRoleMap).map((t) => MANTRA_TOKEN_ORDER.indexOf(t)).filter((i) => i >= 0);
          return idx.length ? Math.min(...idx) : 99;
        }
        const r = ROLE_ORDER.indexOf(p.role);
        return r >= 0 ? r : 99;
      }
      case 'prob': return getPlayProbabilitySortValue(playProbabilityById[String(p.id)]);
      case 'value': return getCurrentValue(p, currentById);
      case 'fixtures': {
        const fx = fixturesByTeam.get(p.team) || [];
        return fx.length ? fx.reduce((a, f) => a + f.difficulty, 0) / fx.length : 99;
      }
      default: return 0;
    }
  };

  const sortedPlayers = useMemo(() => {
    if (!sort.key) return filteredPlayers;
    const mult = sort.dir === 'asc' ? 1 : -1;
    return filteredPlayers
      .map((p) => ({ p, k: sortValue(p, sort.key) }))
      .sort((a, b) => {
        if (a.k < b.k) return -1 * mult;
        if (a.k > b.k) return 1 * mult;
        return normalizeText(a.p.name).localeCompare(normalizeText(b.p.name));
      })
      .map(({ p }) => p);
    // sortValue dipende da questi stessi valori
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPlayers, sort, rolesMode, mantraRoleMap, playProbabilityById, currentById, fixturesByTeam]);

  const handleSort = (key) => {
    setSort((cur) => {
      if (cur.key === key) return { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
      const opt = SORT_OPTIONS.find((o) => o.key === key);
      return { key, dir: opt?.dir || 'asc' };
    });
  };

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [roleFilter, mantraRoleFilter, rolesMode, deferredSearch, sort]);

  const toggleMantraRoleFilter = (token) => {
    setMantraRoleFilter((cur) => {
      const next = new Set(cur);
      if (next.has(token)) next.delete(token);
      else next.add(token);
      return next;
    });
  };

  const visiblePlayers = sortedPlayers.slice(0, visibleCount);

  // Comprare un giocatore = fissarne il costo d'acquisto alla quotazione
  // attuale di QUEL momento (poi modificabile): se più avanti la quotazione
  // cambia, la differenza diventa plusvalenza/minusvalenza. Un giocatore già
  // ceduto e ricomprato è un nuovo acquisto, al prezzo di oggi.
  const freezePurchase = (key) => {
    const player = playersById.get(key);
    if (!player) return;
    setPurchaseById((cur) => (Number.isFinite(cur[key]) && !soldById[key]
      ? cur
      : { ...cur, [key]: getCurrentValue(player, currentById) }));
  };

  const toggleSelect = (playerId) => {
    const key = String(playerId);
    if (!selectedIds.has(key)) freezePurchase(key);
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Usata dal drop nella rosa: aggiunge sempre, non toglie mai (a differenza
  // di toggleSelect, che è pensato per il click sulla riga/checkbox).
  const addToSelection = (playerId) => {
    const key = String(playerId);
    if (!selectedIds.has(key)) freezePurchase(key);
    setSelectedIds((current) => {
      if (current.has(key)) return current;
      const next = new Set(current);
      next.add(key);
      return next;
    });
  };

  // Vendere: esce dalla rosa e incassa la quotazione attuale (modificabile
  // dopo, nella lista "Ceduti"). La X invece toglie il giocatore come se non
  // fosse mai stato comprato (nessun incasso, nessuna plusvalenza).
  const [sellTarget, setSellTarget] = useState(null); // giocatore da cedere (finestra di conferma)

  const sellPlayer = (playerId, salePrice) => {
    const key = String(playerId);
    const player = playersById.get(key);
    if (!player) return;
    const purchase = getPurchaseCost(player, purchaseById, currentById);
    const sale = Number.isFinite(Number(salePrice)) ? Math.max(0, Number(salePrice)) : getCurrentValue(player, currentById);
    setSoldById((cur) => ({ ...cur, [key]: { purchase, sale } }));
    setSelectedIds((current) => {
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  };

  const undoSale = (playerId) => {
    const key = String(playerId);
    const record = soldById[key];
    if (!record) return;
    setPurchaseById((cur) => ({ ...cur, [key]: record.purchase }));
    setSoldById((cur) => {
      const next = { ...cur };
      delete next[key];
      return next;
    });
    setSelectedIds((current) => new Set(current).add(key));
  };

  const handleSalePriceChange = (playerId, value) => {
    const num = Number(value);
    const key = String(playerId);
    setSoldById((cur) => (cur[key] ? { ...cur, [key]: { ...cur[key], sale: Number.isFinite(num) ? Math.max(0, num) : 0 } } : cur));
  };

  const handleCurrentChange = (playerId, value) => {
    const num = Number(value);
    setCurrentById((current) => ({ ...current, [String(playerId)]: Number.isFinite(num) ? Math.max(0, num) : 0 }));
  };

  const handlePlayerDragStart = (e, playerId) => {
    e.dataTransfer.setData('text/plain', String(playerId));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleRosterDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleRosterDrop = (e) => {
    e.preventDefault();
    setIsDragOverRoster(false);
    const id = e.dataTransfer.getData('text/plain');
    if (id) addToSelection(id);
  };

  // "Svuota": riparte da zero, cessioni comprese.
  const clearSelection = () => {
    setSelectedIds(new Set());
    setSoldById({});
  };

  const handleCreditsChange = (playerId, value) => {
    const num = Number(value);
    setPurchaseById((current) => ({ ...current, [String(playerId)]: Number.isFinite(num) ? Math.max(0, num) : 0 }));
  };

  const toggleAnalyzed = (playerId) => {
    setAnalyzedIds((current) => {
      const next = new Set(current);
      const key = String(playerId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const analyzeAll = () => setAnalyzedIds(new Set(selectedIds));
  const analyzeNone = () => setAnalyzedIds(new Set());

  // L'analisi non può mai contenere un giocatore che non è (più) tra i
  // preferiti: se viene tolto dai preferiti (X, click in tabella, "Svuota"),
  // sparisce automaticamente anche dall'analisi, qualunque sia la strada
  // presa per rimuoverlo.
  useEffect(() => {
    setAnalyzedIds((current) => {
      let changed = false;
      const next = new Set();
      current.forEach((id) => {
        if (selectedIds.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : current;
    });
  }, [selectedIds]);

  const selectedPlayers = useMemo(
    () => Array.from(selectedIds).map((id) => playersById.get(id)).filter(Boolean),
    [selectedIds, playersById]
  );

  const soldPlayers = useMemo(
    () => Object.entries(soldById)
      .map(([id, rec]) => ({ player: playersById.get(id), ...rec }))
      .filter((s) => s.player),
    [soldById, playersById]
  );

  // Bilancio di TUTTA la rosa (non solo quella in analisi):
  //  disponibili = crediti iniziali − pagato (rosa + ceduti) + incassato dalle cessioni
  //  realizzate  = incassato − pagato per i ceduti (già nel budget)
  //  potenziali  = quotazione attuale − pagato per chi è in rosa (se vendessi oggi)
  const economy = useMemo(() => {
    let spentHeld = 0;
    let currentHeld = 0;
    selectedPlayers.forEach((p) => {
      spentHeld += getPurchaseCost(p, purchaseById, currentById);
      currentHeld += getCurrentValue(p, currentById);
    });
    const spentSold = soldPlayers.reduce((acc, s) => acc + s.purchase, 0);
    const income = soldPlayers.reduce((acc, s) => acc + s.sale, 0);
    return {
      spentHeld,
      currentHeld,
      spentSold,
      income,
      available: totalCredits - spentHeld - spentSold + income,
      realized: income - spentSold,
      potential: currentHeld - spentHeld,
    };
  }, [selectedPlayers, soldPlayers, purchaseById, currentById, totalCredits]);
  const rosterRef = useRef(null);
  const statsRef = useRef(null);

  const analyzedPlayers = useMemo(
    () => selectedPlayers.filter((p) => analyzedIds.has(String(p.id))),
    [selectedPlayers, analyzedIds]
  );

  const rosterStats = useMemo(() => {
    if (!analyzedPlayers.length) return null;

    const n = analyzedPlayers.length;
    let sumFmBlended = 0;
    let sumMvBlended = 0;
    let sumValue = 0;
    let sumCredits = 0;
    let sumMvCurrent = 0;
    let sumFvCurrent = 0;
    let sumMvPrev = 0;
    let sumFvPrev = 0;
    let prevCount = 0;
    let starters = 0;
    let bench = 0;
    let unknownProbability = 0;
    const byRole = { P: 0, D: 0, C: 0, A: 0 };
    const byMantra = {};
    const teamCounts = {};

    analyzedPlayers.forEach((p) => {
      sumFmBlended += getBlendedStat(p, 'fmarkavg', roleBaselines);
      sumMvBlended += getBlendedStat(p, 'markavg', roleBaselines);
      sumValue += getCurrentValue(p, currentById);
      sumCredits += getPurchaseCost(p, purchaseById, currentById);
      sumMvCurrent += v(p.markavg);
      sumFvCurrent += v(p.fmarkavg);

      if (p.previousSeasonStats) {
        prevCount += 1;
        sumMvPrev += v(p.previousSeasonStats.markavg);
        sumFvPrev += v(p.previousSeasonStats.fmarkavg);
      }

      const role = ROLE_ORDER.includes(p.role) ? p.role : 'A';
      byRole[role] += 1;

      const mantraRaw = mantraRoleMap[normalizeNameText(p.name || '')] || p.role;
      splitRoleTokens(mantraRaw).forEach((token) => {
        byMantra[token] = (byMantra[token] || 0) + 1;
      });

      const probEntry = playProbabilityById[String(p.id)];
      if (!probEntry || !Number.isFinite(Number(probEntry?.prob))) {
        unknownProbability += 1;
      } else if (probEntry.status === 'starter') {
        starters += 1;
      } else {
        bench += 1;
      }

      const team = p.team || 'Sconosciuta';
      teamCounts[team] = (teamCounts[team] || 0) + 1;
    });

    const teamConcentration = Object.entries(teamCounts)
      .map(([team, count]) => ({ team, count }))
      .filter(({ count }) => count >= 2)
      .sort((a, b) => b.count - a.count || a.team.localeCompare(b.team));

    const movementCount = byRole.D + byRole.C + byRole.A;
    // Budget per gli slot ancora da riempire: i crediti davvero disponibili,
    // cioè con le cessioni già incassate (vedi economy).
    const budgetResiduo = economy.available;

    let slotResidui = 0;
    if (rolesMode === 'mantra') {
      slotResidui = Math.max(0, roleLimits.P - byRole.P) + Math.max(0, roleLimits.M - movementCount);
    } else {
      ROLE_ORDER.forEach((r) => { slotResidui += Math.max(0, roleLimits[r] - byRole[r]); });
    }
    const budgetPerSlot = slotResidui > 0 ? budgetResiduo / slotResidui : null;

    return {
      count: n,
      fantamediaPrevista: sumFmBlended / n,
      mediaPrevista: sumMvBlended / n,
      valoreTotale: sumValue,
      sumCredits,
      budgetResiduo,
      mvCurrentAvg: sumMvCurrent / n,
      fvCurrentAvg: sumFvCurrent / n,
      mvPrevAvg: prevCount ? sumMvPrev / prevCount : null,
      fvPrevAvg: prevCount ? sumFvPrev / prevCount : null,
      prevCount,
      byRole,
      byMantra,
      movementCount,
      slotResidui,
      budgetPerSlot,
      starters,
      bench,
      unknownProbability,
      teamConcentration,
    };
  }, [analyzedPlayers, mantraRoleMap, roleBaselines, purchaseById, currentById, economy.available, roleLimits, rolesMode, playProbabilityById]);

  // Se nessuno dei selezionati ha statistiche della stagione precedente (es.
  // rosa fatta solo di esordienti/nuovi acquisti), niente valore "precedente"
  // a zero che sembrerebbe un voto reale: la barra/serie non viene proprio
  // renderizzata.
  const comparisonData = useMemo(() => {
    if (!rosterStats) return [];
    const hasPrev = rosterStats.prevCount > 0;
    return [
      {
        stat: 'Media voto',
        corrente: round1(rosterStats.mvCurrentAvg),
        ...(hasPrev ? { precedente: round1(rosterStats.mvPrevAvg) } : {}),
      },
      {
        stat: 'Fantamedia',
        corrente: round1(rosterStats.fvCurrentAvg),
        ...(hasPrev ? { precedente: round1(rosterStats.fvPrevAvg) } : {}),
      },
    ];
  }, [rosterStats]);

  // AuctionMantraManageTab si aspetta un "roster" di acquisti reali
  // ({ playerId, playerName, playerTeam, credits, playerSnapshot }), non una
  // lista di giocatori: qui si ricostruisce la stessa forma a partire dalla
  // selezione corrente. Il ruolo Mantra granulare (Dc/Ds/ecc.) non è un campo
  // nativo del player — va passato esplicitamente come `mantraRole`, perché
  // altrimenti il componente ripiegherebbe su `playerSnapshot.role` (il
  // classic a lettera singola) e la copertura moduli/compatibilità
  // risulterebbe sbagliata.
  const mantraRosterEntries = useMemo(() => {
    if (rolesMode !== 'mantra') return [];
    return analyzedPlayers.map((p) => ({
      playerId: String(p.id),
      playerName: p.name,
      playerTeam: p.team,
      credits: getPurchaseCost(p, purchaseById, currentById),
      // "Valore" dell'analisi finanziaria = quotazione attuale (plusvalenze coerenti con la rosa).
      valuation: getCurrentValue(p, currentById),
      mantraRole: mantraRoleMap[normalizeNameText(p.name || '')] || p.role,
      playerSnapshot: p,
    }));
  }, [rolesMode, analyzedPlayers, mantraRoleMap, purchaseById, currentById]);

  const chartText = isDark ? '#e5e7eb' : '#111827';
  const gridColor = isDark ? 'rgba(255,255,255,.10)' : 'rgba(17,24,39,.10)';

  const handleExport = async ({ name, teamName, credits }) => {
    if (!user?.uid || !selectedPlayers.length) return;
    setExporting(true);
    setExportError('');
    try {
      const countsByRole = { P: 0, D: 0, C: 0, A: 0 };
      selectedPlayers.forEach((p) => {
        const role = ROLE_ORDER.includes(p.role) ? p.role : 'A';
        countsByRole[role] += 1;
      });
      const movementSelected = countsByRole.D + countsByRole.C + countsByRole.A;

      // Eredita i limiti impostati in fase di setup, alzati solo se la
      // selezione li ha già superati (mai un'asta esportata "sopra il
      // limite" fin dal primo secondo).
      const finalRosterLimits = rolesMode === 'mantra'
        ? { P: Math.max(roleLimits.P, countsByRole.P), M: Math.max(roleLimits.M, movementSelected) }
        : {
          P: Math.max(roleLimits.P, countsByRole.P),
          D: Math.max(roleLimits.D, countsByRole.D),
          C: Math.max(roleLimits.C, countsByRole.C),
          A: Math.max(roleLimits.A, countsByRole.A),
        };

      const roleOverrides = rolesMode === 'mantra'
        ? normalizeRoleOverridesInput(DEFAULT_MANTRA_ROLE_CONFIGURATION)
        : normalizeRoleOverridesInput(FANTAPP_ROLE_OVERRIDES_CONFIGURATION);

      const auctionId = await createAuction(user.uid, {
        name,
        participantsCount: 1,
        credits,
        teamNames: [teamName],
        myTeamIndex: 0,
        myTeamName: teamName,
        rosterLimits: finalRosterLimits,
        rosterMinLimits: finalRosterLimits,
        mantraMovementLimit: rolesMode === 'mantra' ? finalRosterLimits.M : null,
        mantraMovementMinLimit: rolesMode === 'mantra' ? finalRosterLimits.M : null,
        visibility: 'private',
        playersSource: 'FantApp',
        rolesMode,
        roleOverrides,
      });

      await Promise.all(selectedPlayers.map((p) => savePurchase(user.uid, auctionId, {
        playerId: String(p.id),
        playerName: p.name,
        playerTeam: p.team,
        role: p.role,
        teamName,
        credits: getPurchaseCost(p, purchaseById, currentById),
        acquiredAt: Date.now(),
        playerSnapshot: p,
      })));

      navigate(`/aste/${auctionId}`);
    } catch (err) {
      setExportError('Errore durante la creazione dell\'asta. Riprova.');
    } finally {
      setExporting(false);
    }
  };

  const roleLimitsSummary = rolesMode === 'classic'
    ? `P ${roleLimits.P} · D ${roleLimits.D} · C ${roleLimits.C} · A ${roleLimits.A}`
    : `P ${roleLimits.P} · Mov ${roleLimits.M}`;

  return (
    <main className="container sr-page">
      <TopBar />

      <div className="sr-hero">
        <div className="sr-hero__copy">
          <div className="sr-hero__eyebrow">
            <FlaskConical size={16} />
            Simula rosa
          </div>
          <h1>Costruisci e valuta una rosa</h1>
          <p>Seleziona i giocatori dal listone, guarda come si comporta la rosa e, se ti convince, esportala in un'asta vera.</p>
        </div>
      </div>

      {!setupComplete ? (
        <SetupScreen
          rolesMode={rolesMode}
          onRolesModeChange={setRolesMode}
          totalCredits={totalCredits}
          onCreditsChange={(val) => setTotalCredits(Math.max(0, Number(val) || 0))}
          roleLimits={roleLimits}
          onLimitChange={handleLimitChange}
          onConfirm={() => setSetupComplete(true)}
        />
      ) : (
        <>
          <div className="sr-config-bar">
            <span>
              <Trophy size={13} /> {totalCredits} cr · {rolesMode === 'classic' ? 'Classic' : 'Mantra'} · {roleLimitsSummary}
            </span>
            <div className="sr-config-bar__actions">
              <button type="button" onClick={() => setShowImportModal(true)}>
                <FileSpreadsheet size={13} /> Importa da Excel
              </button>
              <button type="button" onClick={() => setSetupComplete(false)}>
                <Settings2 size={13} /> Modifica impostazioni
              </button>
            </div>
          </div>

          <SimulaRosaExcelImport
            open={showImportModal}
            onClose={() => setShowImportModal(false)}
            players={players}
            totalCredits={totalCredits}
            hasSelection={selectedIds.size > 0}
            onImport={handleExcelImport}
          />

          <div className="sr-top-grid">
            <section className="sr-left glass">
              <div className="sr-left__toolbar">
                <div className="sr-search">
                  <Search size={15} />
                  <input
                    type="text"
                    placeholder="Cerca giocatore o squadra..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button type="button" onClick={() => setSearch('')} aria-label="Cancella ricerca">
                      <X size={14} />
                    </button>
                  )}
                </div>

                {rolesMode === 'mantra' ? (
                  <div className="sr-role-filters" role="group" aria-label="Filtra per ruolo Mantra (più ruoli insieme)">
                    <button
                      type="button"
                      className={`sr-role-filter ${mantraRoleFilter.size === 0 ? 'is-active' : ''}`}
                      onClick={() => setMantraRoleFilter(new Set())}
                    >
                      Tutti
                    </button>
                    {MANTRA_TOKEN_ORDER.map((token) => {
                      const active = mantraRoleFilter.has(token);
                      const theme = MANTRA_ROLE_THEME[token] || MANTRA_ROLE_THEME.A;
                      return (
                        <button
                          key={token}
                          type="button"
                          aria-pressed={active}
                          className={`sr-role-filter sr-role-filter--mantra ${active ? 'is-active' : ''}`}
                          style={active ? { background: theme.bg, borderColor: theme.border, color: theme.color } : undefined}
                          onClick={() => toggleMantraRoleFilter(token)}
                        >
                          {token}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="sr-role-filters">
                    {ROLE_FILTERS.map((role) => (
                      <button
                        key={role}
                        type="button"
                        className={`sr-role-filter ${roleFilter === role ? 'is-active' : ''}`}
                        onClick={() => setRoleFilter(role)}
                      >
                        {role === 'all' ? 'Tutti' : role}
                      </button>
                    ))}
                  </div>
                )}
                {/* Su telefono alcune colonne sono nascoste: l'ordinamento passa da qui. */}
                <label className="sr-sort-select">
                  <span>Ordina per</span>
                  <select
                    value={sort.key ? `${sort.key}:${sort.dir}` : ''}
                    onChange={(e) => {
                      const [key, dir] = e.target.value.split(':');
                      setSort(key ? { key, dir } : { key: null, dir: 'asc' });
                    }}
                  >
                    <option value="">Ordine del listone</option>
                    {SORT_OPTIONS.map((o) => (
                      <React.Fragment key={o.key}>
                        <option value={`${o.key}:${o.dir}`}>{o.label}{o.dir === 'desc' ? ' (più alta prima)' : ''}</option>
                        <option value={`${o.key}:${o.dir === 'asc' ? 'desc' : 'asc'}`}>
                          {o.key === 'fixtures' ? 'Calendario (più difficile prima)' : `${o.label} (${o.dir === 'asc' ? 'inverso' : 'più bassa prima'})`}
                        </option>
                      </React.Fragment>
                    ))}
                  </select>
                </label>
              </div>

              {loading ? (
                <div className="sr-empty"><Loader2 size={18} className="spin" /> Caricamento giocatori...</div>
              ) : (
                <>
                  <div className="sr-table-wrap">
                    <table className="sr-table">
                      <thead>
                        <tr>
                          <th className="sr-th--check" aria-hidden="true" />
                          <SortHeader className="sr-col-team" label="Squadra" sortKey="team" sort={sort} onSort={handleSort} />
                          <SortHeader label="Giocatore" sortKey="name" sort={sort} onSort={handleSort} />
                          <SortHeader label={rolesMode === 'classic' ? 'Classic' : 'Mantra'} sortKey="role" sort={sort} onSort={handleSort} />
                          <SortHeader label="Titolarità" sortKey="prob" sort={sort} onSort={handleSort} />
                          <SortHeader className="sr-col-value" label="Quot." sortKey="value" sort={sort} onSort={handleSort} />
                          <SortHeader className="sr-col-fixtures" label="Prossime 3" sortKey="fixtures" sort={sort} onSort={handleSort} />
                        </tr>
                      </thead>
                      <tbody>
                        {visiblePlayers.map((p) => {
                          const checked = selectedIds.has(String(p.id));
                          const role = ROLE_ORDER.includes(p.role) ? p.role : 'A';
                          return (
                            <tr
                              key={p.id}
                              className={checked ? 'is-selected' : ''}
                              draggable
                              onDragStart={(e) => handlePlayerDragStart(e, p.id)}
                              onClick={() => toggleSelect(p.id)}
                            >
                              <td className="sr-td--check">
                                <input type="checkbox" checked={checked} onChange={() => toggleSelect(p.id)} onClick={(e) => e.stopPropagation()} />
                              </td>
                              <td className="sr-col-team">
                                <div className="sr-team-cell">
                                  <TeamBadge team={p.team} size={32} showTooltip={false} />
                                  <span>{p.team}</span>
                                </div>
                              </td>
                              <td className="sr-name-cell">
                                <span className="sr-name-cell__name">{p.name}</span>
                                {/* Su telefono la colonna Squadra è nascosta: la squadra va sotto il nome. */}
                                <span className="sr-name-cell__team">
                                  {p.team}<span className="sr-name-cell__value"> · {getCurrentValue(p, currentById)} cr</span>
                                </span>
                                {/* …e così le prossime partite (colonna nascosta su telefono). */}
                                <span className="sr-name-cell__fix"><FixtureMini team={p.team} fixtures={fixturesByTeam.get(p.team)} /></span>
                              </td>
                              <td>
                                {rolesMode === 'classic'
                                  ? <RoleChip role={role} />
                                  : <MantraRoleTokens player={p} mantraRoleMap={mantraRoleMap} />}
                              </td>
                              <td><TitolaritaChip entry={playProbabilityById[String(p.id)]} /></td>
                              <td className="sr-col-value">{getCurrentValue(p, currentById)} cr</td>
                              <td className="sr-col-fixtures"><FixtureMini team={p.team} fixtures={fixturesByTeam.get(p.team)} /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {!visiblePlayers.length && (
                      <div className="sr-empty">Nessun giocatore trovato con questi filtri.</div>
                    )}
                  </div>

                  {filteredPlayers.length > visibleCount && (
                    <button
                      type="button"
                      className="sr-load-more"
                      onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    >
                      Mostra altri giocatori ({filteredPlayers.length - visibleCount} rimanenti)
                    </button>
                  )}
                </>
              )}
            </section>

            <section
              ref={rosterRef}
              className={`sr-roster glass ${isDragOverRoster ? 'is-drag-over' : ''}`}
              onDragOver={handleRosterDragOver}
              onDragEnter={() => setIsDragOverRoster(true)}
              onDragLeave={() => setIsDragOverRoster(false)}
              onDrop={handleRosterDrop}
            >
              <div className="sr-roster__head">
                <div>
                  <Users size={16} />
                  <strong>Rosa selezionata</strong>
                  <span>
                    {selectedPlayers.length} preferiti
                    {selectedPlayers.length > 0 ? ` · ${analyzedPlayers.length} in analisi` : ''}
                  </span>
                </div>
                {selectedPlayers.length > 0 && (
                  <span className="sr-roster__headActions">
                    <button type="button" onClick={analyzedIds.size >= selectedIds.size ? analyzeNone : analyzeAll}>
                      {analyzedIds.size >= selectedIds.size ? 'Nessuna analisi' : 'Analizza tutti'}
                    </button>
                    <button type="button" onClick={clearSelection}>Svuota</button>
                  </span>
                )}
              </div>

              {(selectedPlayers.length > 0 || soldPlayers.length > 0) && (
                <div className="sr-economy">
                  <div className={`sr-economy__cell ${economy.available < 0 ? 'is-neg' : ''}`}>
                    <span>Crediti disponibili</span>
                    <b>{Math.round(economy.available)}</b>
                    <em>su {totalCredits} iniziali</em>
                  </div>
                  <div className="sr-economy__cell">
                    <span>Valore attuale rosa</span>
                    <b>{Math.round(economy.currentHeld)}</b>
                    <em>pagata {Math.round(economy.spentHeld)}</em>
                  </div>
                  <div className={`sr-economy__cell ${economy.potential > 0 ? 'is-pos' : economy.potential < 0 ? 'is-neg' : ''}`}>
                    <span>Plusvalenza potenziale</span>
                    <b>{fmtDelta(economy.potential)}</b>
                    <em>se vendessi tutti oggi</em>
                  </div>
                  <div className={`sr-economy__cell ${economy.realized > 0 ? 'is-pos' : economy.realized < 0 ? 'is-neg' : ''}`}>
                    <span>Plusvalenze realizzate</span>
                    <b>{fmtDelta(economy.realized)}</b>
                    <em>{soldPlayers.length ? `${soldPlayers.length} ceduti, già nel budget` : 'nessuna cessione'}</em>
                  </div>
                </div>
              )}

              {selectedPlayers.length > 0 && (
                <p className="sr-roster__hint">
                  Per ogni giocatore: <b>costo d'acquisto</b> (quanto l'hai pagato) e <b>quotazione attuale</b> (a quanto
                  lo vendi oggi). <b>Vendi</b> incassa la quotazione attuale; la <b>×</b> lo toglie come se non l'avessi
                  mai comprato. La spunta lo include nelle statistiche qui sotto.
                </p>
              )}

              {!selectedPlayers.length ? (
                <div className="sr-roster__empty">
                  <Shirt size={22} />
                  <span>Seleziona i giocatori dalla lista o trascinali qui</span>
                </div>
              ) : (
                <div className="sr-roster__list sr-table-wrap">
                  {selectedPlayers.map((p) => {
                    const role = ROLE_ORDER.includes(p.role) ? p.role : 'A';
                    const isAnalyzed = analyzedIds.has(String(p.id));
                    const purchase = getPurchaseCost(p, purchaseById, currentById);
                    const current = getCurrentValue(p, currentById);
                    const delta = current - purchase;
                    return (
                      <div key={p.id} className={`sr-roster__row ${isAnalyzed ? 'is-analyzed' : ''}`}>
                        <div className="sr-roster__main">
                          <input
                            type="checkbox"
                            className="sr-roster__analyzeCheck"
                            checked={isAnalyzed}
                            onChange={() => toggleAnalyzed(p.id)}
                            aria-label={`Includi ${p.name} nell'analisi`}
                          />
                          <TeamBadge team={p.team} size={26} showTooltip={false} />
                          <span className="sr-roster__name">{p.name}</span>
                          {rolesMode === 'classic'
                            ? <RoleChip role={role} />
                            : <MantraRoleTokens player={p} mantraRoleMap={mantraRoleMap} />}
                          <span className={`sr-delta ${delta > 0 ? 'is-pos' : delta < 0 ? 'is-neg' : ''}`} title="Quotazione attuale − costo d'acquisto">
                            {fmtDelta(delta)}
                          </span>
                          <button type="button" className="sr-roster__sell" onClick={() => setSellTarget(p)}>
                            Vendi
                          </button>
                          <button
                            type="button"
                            className="sr-roster__remove"
                            onClick={() => toggleSelect(p.id)}
                            aria-label={`Togli ${p.name} (non comprato)`}
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <div className="sr-roster__prices">
                          <label>
                            <span>Acquisto</span>
                            <NumberStepper
                              className="sr-credits-input"
                              value={purchase}
                              onChange={(val) => handleCreditsChange(p.id, val)}
                            />
                          </label>
                          <label>
                            <span>Attuale</span>
                            <NumberStepper
                              className="sr-credits-input"
                              value={current}
                              onChange={(val) => handleCurrentChange(p.id, val)}
                            />
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {soldPlayers.length > 0 && (
                <div className="sr-sold">
                  <button type="button" className="sr-sold__head" onClick={() => setSoldOpen((o) => !o)}>
                    <span>Ceduti ({soldPlayers.length})</span>
                    <b className={economy.realized > 0 ? 'is-pos' : economy.realized < 0 ? 'is-neg' : ''}>
                      {fmtDelta(economy.realized)} cr
                    </b>
                    <ChevronDown size={14} className={soldOpen ? 'is-open' : ''} />
                  </button>
                  {soldOpen && soldPlayers.map(({ player, purchase, sale }) => {
                    const delta = sale - purchase;
                    return (
                      <div key={player.id} className="sr-sold__row">
                        <span className="sr-roster__name">{player.name}</span>
                        <span className="sr-sold__paid">pagato {purchase}</span>
                        <label className="sr-sold__sale">
                          <span>venduto a</span>
                          <NumberStepper
                            className="sr-credits-input"
                            value={sale}
                            onChange={(val) => handleSalePriceChange(player.id, val)}
                          />
                        </label>
                        <span className={`sr-delta ${delta > 0 ? 'is-pos' : delta < 0 ? 'is-neg' : ''}`}>{fmtDelta(delta)}</span>
                        <button type="button" className="sr-roster__remove" onClick={() => undoSale(player.id)} aria-label={`Annulla la cessione di ${player.name}`} title="Annulla cessione">
                          <RotateCcw size={13} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

          {/* Solo telefono: la rosa e le statistiche stanno sotto una lista lunga,
              quindi un riepilogo fisso in basso dice cosa hai preso e ci porta. */}
          {(selectedPlayers.length > 0 || soldPlayers.length > 0) && (
            <div className="sr-mobile-bar" role="region" aria-label="Riepilogo rosa">
              <span className="sr-mobile-bar__info">
                <b>{selectedPlayers.length}</b> in rosa · disp.{' '}
                <b className={economy.available < 0 ? 'is-warn' : ''}>{Math.round(economy.available)}</b> cr
                {economy.potential !== 0 && (
                  <> · <b className={economy.potential > 0 ? 'is-pos' : 'is-warn'}>{fmtDelta(economy.potential)}</b></>
                )}
              </span>
              <button type="button" onClick={() => rosterRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                <Users size={14} /> Rosa
              </button>
              <button type="button" onClick={() => statsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                <Trophy size={14} /> Statistiche
              </button>
            </div>
          )}

          <section className="sr-stats" ref={statsRef}>
              {!rosterStats ? (
                <div className="sr-stats-empty glass">
                  <Shirt size={28} />
                  <strong>{selectedPlayers.length ? 'Nessun giocatore da analizzare' : 'Nessun giocatore selezionato'}</strong>
                  <span>
                    {selectedPlayers.length
                      ? 'Hai dei preferiti in rosa, ma nessuno è spuntato per l\'analisi: spunta almeno un giocatore nel pannello qui sopra.'
                      : 'Seleziona o trascina dei giocatori nella rosa qui sopra per vedere le statistiche.'}
                  </span>
                </div>
              ) : (
                <>
                  <div className="sr-stats-row">
                    <StatTile label="Giocatori" value={rosterStats.count} />
                    <StatTile label="Fantamedia prevista" value={fmt(rosterStats.fantamediaPrevista)} hint="media pesata" />
                    <StatTile label="Media voto prevista" value={fmt(rosterStats.mediaPrevista)} hint="media pesata" />
                    <StatTile
                      label="Titolari attesi"
                      value={`${rosterStats.starters}/${rosterStats.count}`}
                      hint={
                        rosterStats.unknownProbability === rosterStats.count
                          ? 'dati non disponibili'
                          : [
                            rosterStats.bench > 0 ? `${rosterStats.bench} in dubbio` : null,
                            rosterStats.unknownProbability > 0 ? `${rosterStats.unknownProbability} N/D` : null,
                          ].filter(Boolean).join(' · ') || 'tutti titolari secondo le probabili'
                      }
                      warn={rosterStats.bench > 0}
                    />
                    <StatTile
                      label="Pagati (in analisi)"
                      value={`${Math.round(rosterStats.sumCredits)} cr`}
                      hint={`disponibili ${Math.round(economy.available)} cr`}
                      warn={economy.available < 0}
                    />
                    <StatTile
                      label="Valore attuale (in analisi)"
                      value={`${Math.round(rosterStats.valoreTotale)} cr`}
                      hint={`${fmtDelta(rosterStats.valoreTotale - rosterStats.sumCredits)} cr vs pagato`}
                    />
                  </div>

                  <div className="sr-stats-panels">
                  <div className="sr-panel glass">
                    <div className="sr-panel__head">
                      <Trophy size={16} />
                      <div>
                        <strong>Copertura per reparto</strong>
                        <span>Giocatori selezionati per ruolo, rispetto ai limiti che hai impostato</span>
                      </div>
                    </div>

                    {rosterStats.slotResidui > 0 && rosterStats.budgetPerSlot !== null && (
                      <div className="sr-budget-hint">
                        Ti restano <strong>{rosterStats.slotResidui}</strong> slot da riempire: in media{' '}
                        <strong className={rosterStats.budgetPerSlot < 0 ? 'is-warn' : ''}>{Math.round(rosterStats.budgetPerSlot)} cr</strong> a disposizione per ciascuno.
                      </div>
                    )}

                    <div className="sr-coverage-list">
                      {rolesMode === 'classic' ? (
                        ROLE_ORDER.map((role) => (
                          <CoverageBar
                            key={role}
                            label={ROLE_LABELS[role]}
                            count={rosterStats.byRole[role]}
                            max={roleLimits[role]}
                            color={ROLE_CHIP_THEME[role].backgroundColor}
                          />
                        ))
                      ) : (
                        <>
                          <CoverageBar label="Portieri" count={rosterStats.byRole.P} max={roleLimits.P} color={ROLE_CHIP_THEME.P.backgroundColor} />
                          <CoverageBar label="Movimento" count={rosterStats.movementCount} max={roleLimits.M} color="#7c5cff" />
                        </>
                      )}
                    </div>

                    {rolesMode === 'mantra' && (
                      <div className="sr-mantra-coverage">
                        <span className="sr-mantra-coverage__label">Distribuzione ruoli Mantra</span>
                        <div className="sr-mantra-coverage__chips">
                          {MANTRA_TOKEN_ORDER.filter((token) => rosterStats.byMantra[token]).map((token) => {
                            const theme = MANTRA_ROLE_THEME[token] || MANTRA_ROLE_THEME.A;
                            return (
                              <span
                                key={token}
                                className="sr-mantra-coverage__chip"
                                style={{ background: theme.bg, border: `1px solid ${theme.border}`, color: theme.color }}
                              >
                                {token} × {rosterStats.byMantra[token]}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="sr-panel glass">
                    <div className="sr-panel__head">
                      <ShieldCheck size={16} />
                      <div>
                        <strong>Concentrazione squadre</strong>
                        <span>Quanti giocatori selezionati vengono dallo stesso club — troppa concentrazione espone al calendario di una singola squadra.</span>
                      </div>
                    </div>
                    {rosterStats.teamConcentration.length ? (
                      <div className="sr-team-concentration">
                        {rosterStats.teamConcentration.map(({ team, count }) => {
                          const heavy = count > TEAM_CONCENTRATION_THRESHOLD;
                          return (
                            <AppTooltip
                              key={team}
                              as="span"
                              className={`sr-team-concentration__chip ${heavy ? 'is-heavy' : ''}`}
                              content={heavy ? `Più di ${TEAM_CONCENTRATION_THRESHOLD} giocatori dal ${team}: rosa esposta al calendario di quella squadra.` : undefined}
                            >
                              <TeamBadge team={team} size={18} showTooltip={false} />
                              {team} × {count}
                              {heavy && <AlertTriangle size={12} />}
                            </AppTooltip>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="sr-team-concentration__empty">Nessuna concentrazione: al massimo un giocatore per squadra tra i selezionati.</span>
                    )}
                  </div>

                  <div className="sr-panel glass sr-panel--wide">
                    <div className="sr-panel__head">
                      <FlaskConical size={16} />
                      <div>
                        <strong>Stagione corrente vs precedente</strong>
                        <span>
                          {rosterStats.prevCount === 0
                            ? 'Nessuno dei giocatori selezionati ha dati della stagione precedente (rosa di soli esordienti/nuovi acquisti): mostrata solo la stagione corrente.'
                            : rosterStats.prevCount < rosterStats.count
                              ? `Media voto e fantamedia a confronto (dati precedenti disponibili per ${rosterStats.prevCount}/${rosterStats.count} giocatori)`
                              : 'Media voto e fantamedia a confronto'}
                        </span>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={comparisonData} margin={{ top: 10, right: 8, left: 0, bottom: 4 }}>
                        <CartesianGrid stroke={gridColor} strokeDasharray="4 4" vertical={false} />
                        <XAxis dataKey="stat" tick={{ fill: chartText, fontSize: 12 }} tickLine={false} />
                        <YAxis tick={{ fill: chartText, fontSize: 11 }} width={32} />
                        <RechartsTooltip content={<ChartTooltip isDark={isDark} />} cursor={{ fill: 'rgba(124,92,255,0.06)' }} />
                        <Legend wrapperStyle={{ color: chartText, fontSize: 12 }} />
                        <Bar dataKey="corrente" name="Stagione corrente" fill="#7c5cff" radius={[6, 6, 0, 0]} />
                        {rosterStats.prevCount > 0 && (
                          <Bar dataKey="precedente" name="Stagione precedente" fill="#31d0aa" radius={[6, 6, 0, 0]} />
                        )}
                      </BarChart>
                    </ResponsiveContainer>

                    <div className="sr-table-wrap sr-table-wrap--compact">
                      <table className="sr-table sr-table--compact">
                        <thead>
                          <tr>
                            <th>Giocatore</th>
                            <th>{rolesMode === 'classic' ? 'Classic' : 'Mantra'}</th>
                            <th>MV corr.</th>
                            <th>MV prec.</th>
                            <th>FV corr.</th>
                            <th>FV prec.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyzedPlayers.map((p) => {
                            const role = ROLE_ORDER.includes(p.role) ? p.role : 'A';
                            const prev = p.previousSeasonStats;
                            return (
                              <tr key={p.id}>
                                <td>
                                  <AppTooltip as="span" content={`${p.name} (${p.team})`}>{p.name}</AppTooltip>
                                </td>
                                <td>
                                  {rolesMode === 'classic'
                                    ? <RoleChip role={role} />
                                    : <MantraRoleTokens player={p} mantraRoleMap={mantraRoleMap} />}
                                </td>
                                <td>{p.markavg !== null && p.markavg !== undefined ? fmt(p.markavg) : '—'}</td>
                                <td>{prev?.markavg !== null && prev?.markavg !== undefined ? fmt(prev.markavg) : '—'}</td>
                                <td>{p.fmarkavg !== null && p.fmarkavg !== undefined ? fmt(p.fmarkavg) : '—'}</td>
                                <td>{prev?.fmarkavg !== null && prev?.fmarkavg !== undefined ? fmt(prev.fmarkavg) : '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  </div>

                  {rolesMode === 'mantra' && (
                    <AuctionMantraManageTab
                      roster={mantraRosterEntries}
                      rolesMode="mantra"
                      playProbabilityById={playProbabilityById}
                      onOpenPlayerDetail={openPlayerDetailNewTab}
                    />
                  )}

                  <button type="button" className="sr-export-btn" onClick={() => setShowExportModal(true)}>
                    <ExternalLink size={16} />
                    Esporta in un'asta
                  </button>
                </>
              )}
          </section>
        </>
      )}

      <SellModal
        player={sellTarget}
        purchase={sellTarget ? getPurchaseCost(sellTarget, purchaseById, currentById) : 0}
        current={sellTarget ? getCurrentValue(sellTarget, currentById) : 0}
        onClose={() => setSellTarget(null)}
        onConfirm={(sale) => { sellPlayer(sellTarget.id, sale); setSellTarget(null); }}
      />

      <ExportModal
        open={showExportModal}
        onClose={() => { if (!exporting) setShowExportModal(false); }}
        // Le plusvalenze già incassate allargano il budget: l'asta esportata
        // parte dagli stessi crediti disponibili che vedi qui.
        defaultCredits={Math.max(1, Math.round(totalCredits + economy.realized))}
        onConfirm={handleExport}
        exporting={exporting}
        error={exportError}
      />
    </main>
  );
}
